import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from 'react-bootstrap';
import formatXML from 'xml-formatter';
import { getDocumentById, getDocumentTypes } from '../../../../api/v2/load';
import DocumentNavigation from './DocumentNavigation';
import RenderXLSDocument from './RenderXLSDocument';
import RenderPDFDocument from './RenderPDFDocument';
import { Input, Select } from '../../../Input/Input';
import ErrorIcon from '@mui/icons-material/Error';
import BPWarningModal from '../../../BusinessPartners/BPWarningModal';
import styles from './DocumentViewer.module.css';
import Tooltip from '../../../../components/Tooltip';

const cm8Filetypes = ['.XLSX', '.PNG', '.JPEG', '.JPG', '.XLS', '.PDF'];

const RenderLoadingSpinner = () => {
  return (
    <div className={styles.loadingContainer}>
      <Spinner animation="border" size="lg" />
      <div>Please wait...</div>
    </div>
  );
};

const RenderDocument = ({ fileBuffer, filetype }) => {
  if (fileBuffer) {
    if (filetype === 'xlsx' || filetype === 'xls' || filetype === 'csv') {
      return <RenderXLSDocument fileBuffer={fileBuffer} />;
    }

    if (filetype === 'pdf') {
      return <RenderPDFDocument fileBuffer={fileBuffer} />;
    }
    if (filetype === 'json') {
      // convert fileBuffer to string and parse it to JSON
      const jsonString = fileBuffer.toString();
      const jsonData = JSON.parse(jsonString);
      return <pre className={styles.codeBlock}>{JSON.stringify(jsonData, null, 2)}</pre>;
    }
    if (filetype === 'xml') {
      const xml = fileBuffer.toString();

      const formattedXml = formatXML(xml, {
        indentation: '  ',
        collapseContent: true,
      });

      return <pre className={styles.codeBlock}>{formattedXml}</pre>;
    }
    if (filetype === 'png' || filetype === 'jpeg' || filetype === 'jpg') {
      return (
        <img
          alt=""
          src={`data:image/${filetype};base64,${fileBuffer.toString('base64')}`}
          style={{ maxWidth: '100%', minWidth: '50%' }}
        />
      );
    }

    return <div className={styles.previewError}>Unable to preview file. File type not supported.</div>;
  } else {
    return <div className={styles.previewError}>Unable to preview file.</div>;
  }
};

const RenderSessionStatus = ({ isActive, portalRef }) => {
  return (
    <div className={`${styles.sessionPanel} ${isActive ? styles.activeBg : styles.inactiveBg}`}>
      {isActive ? (
        <div>Session with {portalRef}</div>
      ) : (
        <div>
          <div className="bold">NO CABIE SESSION</div>
          <div>Please close this window</div>
        </div>
      )}
    </div>
  );
};

const handleDownloadFile = (fileBuffer, filename = 'file.txt') => {
  if (!fileBuffer) return;
  const fileURL = window.URL.createObjectURL(new Blob([fileBuffer]));
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.href = fileURL;
  a.download = filename;
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(fileURL);
    document.body.removeChild(a);
  }, 0);
};

const shouldDisableInvoiceField = (documentType) => !['Commercial Document', 'Invoice'].includes(documentType);

const DocumentViewer = ({ portalRef, documents, currentDocument, channelName }) => {
  const initialIndex = documents.findIndex((doc) => doc.id === currentDocument.id);
  const [documentIndex, setDocumentIndex] = useState(initialIndex !== -1 ? initialIndex : 0);
  const [fileBuffer, setFileBuffer] = useState(null);
  const [filetype, setFiletype] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ message: '', error: false });
  const [isConnectionActive, setIsConnectionActive] = useState(true);

  const [documentDetails, setDocumentDetails] = useState({
    invoiceNumber: '',
    documentType: '',
    cm8: false,
  });
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const channelRef = useRef(null);
  const pendingNavigationRef = useRef(null);
  const rootRef = useRef(null);
  const isDirtyRef = useRef(false);
  const saveSuccessTimeoutRef = useRef(null);
  const [modalContainer, setModalContainer] = useState(null);

  const currentDoc = documents[documentIndex];
  const docIsCm8Filetype = cm8Filetypes.some((type) => currentDoc.filename.toUpperCase().endsWith(type));

  const disableInvoice = shouldDisableInvoiceField(documentDetails.documentType);

  const { data: documentTypes = [] } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: getDocumentTypes,
  });

  const { data: documentData, isLoading } = useQuery({
    queryKey: ['document', currentDoc?.id],
    queryFn: () => getDocumentById(currentDoc.id),
    enabled: !!currentDoc?.id,
    select: ([doc = {}]) => doc,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!documentData) return;
    const documentType = documentData.documentType || '';

    setDocumentDetails({
      invoiceNumber: shouldDisableInvoiceField(documentType) ? '' : documentData.invoiceNumber || '',
      documentType,
      cm8: documentData.cm8 || false,
    });

    setFileBuffer(Buffer.from(documentData?.binaryData?.data));
    setFiletype(currentDoc?.filename.split('.').slice(-1)[0].toLowerCase() ?? null);
    isDirtyRef.current = false;
  }, [documentData]);

  // child window setup: modal portal target and close-with-unsaved-changes guard
  useEffect(() => {
    if (!rootRef.current) return;
    const childWindow = rootRef.current.ownerDocument.defaultView;
    setModalContainer(childWindow.document.body);
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) e.preventDefault();
    };
    childWindow.addEventListener('beforeunload', handleBeforeUnload);
    return () => childWindow.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const cleanUp = () => {
      if (channelRef.current) {
        channelRef.current.postMessage({ type: 'child-closed' });
        channelRef.current.close();
      }
    };
    window.addEventListener('beforeunload', cleanUp);

    return () => {
      cleanUp();
      window.removeEventListener('beforeunload', cleanUp);
    };
  }, []);

  useEffect(() => {
    const lockName = `lock-${channelName}`;
    const abortController = new AbortController();
    // Fires immediately when the parent tab is closed or navigated away
    navigator.locks
      .request(lockName, { mode: 'shared', signal: abortController.signal }, () => {
        setIsConnectionActive(false);
      })
      .catch(() => {});
    return () => abortController.abort();
  }, [channelName]);

  useEffect(() => {
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    channel.addEventListener('message', (event) => {
      const { type, payload } = event.data || {};
      if (type === 'select-document' && payload.documentId) {
        onSelectDocument(payload.documentId);
      }
      if (type === 'document-saved') {
        showToastMessage('Document saved successfully', false);
        isDirtyRef.current = false;
      }
      if (type === 'document-save-error') {
        showToastMessage('An error occurred', true);
      }
    });

    return () => channel.close();
  }, [channelName]);

  const showToastMessage = (message, error = false) => {
    setIsSaving(false);
    setToast({ message, error });
    saveSuccessTimeoutRef.current = setTimeout(() => setToast({ message: '', error: false }), 5000);
  };

  const navigateWithConfirmation = (action) => {
    if (isDirtyRef.current) {
      pendingNavigationRef.current = action;
      setShowUnsavedModal(true);
    } else {
      action();
    }
  };

  const confirmNavigation = () => {
    setShowUnsavedModal(false);
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  };

  const cancelNavigation = () => {
    setShowUnsavedModal(false);
    pendingNavigationRef.current = null;
  };

  const navigateToIndex = (index) => {
    clearTimeout(saveSuccessTimeoutRef.current);
    setToast({ message: '', error: false });
    navigateWithConfirmation(() => setDocumentIndex(index));
  };

  const onSelectDocument = (documentId) => {
    const index = documents.findIndex((doc) => doc.id === documentId);
    if (index !== -1) navigateToIndex(index);
  };

  const onPrevDocument = () => {
    if (documentIndex > 0) navigateToIndex(documentIndex - 1);
  };

  const onNextDocument = () => {
    if (documentIndex < documents.length - 1) navigateToIndex(documentIndex + 1);
  };

  const saveDocumentDetails = () => {
    if (channelRef?.current) {
      setIsSaving(true);
      channelRef.current.postMessage({
        type: 'update-document',
        payload: { id: currentDoc.id, ...documentDetails },
      });
    }
  };

  const handleInputChange = (field, value) => {
    isDirtyRef.current = true;
    setDocumentDetails((prevDetails) => ({
      ...prevDetails,
      [field]: value,
    }));
  };

  const closeWindow = () => rootRef.current?.ownerDocument.defaultView.close();

  const disableButtons = isLoading || isSaving;

  return (
    <div ref={rootRef} className={styles.root}>
      {toast.message && (
        <div className={`${styles.toastMessage} ${toast.error ? styles.errorMessage : ''}`}>{toast.message}</div>
      )}
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <div className={`${styles.reference} ${isConnectionActive ? styles.activeText : styles.inactiveText}`}>
            {!isConnectionActive && <ErrorIcon className={styles.errorIcon} />}
            {portalRef || 'No reference found'}
          </div>
          <div className={styles.formContainer}>
            <div className={styles.textField}>
              <Input
                label="Invoice Number"
                value={documentDetails.invoiceNumber}
                onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                disabled={disableInvoice || !isConnectionActive || disableButtons}
                maxLength={35}
              />
            </div>
            <div className={styles.textField}>
              <Select
                label="Document Type"
                data={documentDetails.documentType}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange('documentType', value);
                  if (shouldDisableInvoiceField(value)) {
                    handleInputChange('invoiceNumber', '');
                  }
                }}
                disabled={!isConnectionActive || disableButtons}
                options={documentTypes.map(({ data: type }) => ({ id: type, data: type }))}
              />
            </div>
            <div className={styles.otherField}>
              <Tooltip title={docIsCm8Filetype ? null : 'This file type is not supported by CM8'}>
                <label
                  className="d-flex align-items-center gap-2"
                  style={{
                    fontSize: '1rem',
                    cursor: !isConnectionActive || disableButtons || !docIsCm8Filetype ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={documentDetails.cm8}
                    onChange={(e) => handleInputChange('cm8', e.target.checked)}
                    disabled={!isConnectionActive || disableButtons || !docIsCm8Filetype}
                  />{' '}
                  <div>Select for CM8</div>
                </label>
              </Tooltip>
            </div>
            <div className={styles.otherField}>
              <button
                type="button"
                className="blue-button-teams green-btn"
                onClick={saveDocumentDetails}
                disabled={!isConnectionActive || disableButtons}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <DocumentNavigation
            pageNo={documentIndex + 1}
            totalDocs={documents.length}
            onNext={onNextDocument}
            onPrev={onPrevDocument}
            onDownloadFile={() => handleDownloadFile(fileBuffer, currentDoc?.filename || 'file.txt')}
            onClose={closeWindow}
            disabled={!isConnectionActive || disableButtons}
          />
          <RenderSessionStatus isActive={isConnectionActive} portalRef={portalRef} />
        </div>
      </div>
      <div className={styles.documentArea}>
        {isLoading ? <RenderLoadingSpinner /> : <RenderDocument fileBuffer={fileBuffer} filetype={filetype} />}
      </div>

      <BPWarningModal
        show={showUnsavedModal}
        onHide={cancelNavigation}
        onConfirm={confirmNavigation}
        container={modalContainer}
      />
    </div>
  );
};

export default DocumentViewer;
